import { createLabManifest } from "@/helpers/lab_utils/labManifest";
import {
  createLabPassage,
  completionPassageExtras
} from "@/helpers/lab_utils/labPassage";

const passages = [
  createLabPassage({
    id: "start",
    progressLabel: "Welcome",
    title: "Atomic Emission Spectrometer Lab",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "CHE 417/517: Analytical Instrumentation in ChE, BioE and EnvE"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Examining the Instrumental Response of an ICP-AES to Ca2+, Mg2+, and Na+ ions."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Note: ICP-AES (Inductively Coupled Plasma-Atomic Emission Spectrometer) = ICP-OES (Inductively Coupled Plasma-Optical Emission Spectrometer)."
                }
              ]
            }
          ]
        }
      },
      {
        type: "custom",
        name: "skip-to-measurements",
        skipTo: "explore-instrument"
      }
    ],
    choices: [{ text: "Next", to: "objective" }]
  }),

  createLabPassage({
    id: "objective",
    progressLabel: "Objective",
    title: "Objective",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The purpose of this experiment is to demonstrate sample analysis using an Atomic Emission Spectroscopy (AES) system using an Inductively Coupled Plasma (ICP) flame."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "introduction" }]
  }),

  createLabPassage({
    id: "introduction",
    progressLabel: "Introduction",
    title: "Introduction: Atomic Emission Spectroscopy",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The observation of atomic emission has occurred since ancient times when humans first started working with fire, and has been a recognized scientific tool since the days when the alchemists recognized colored glows that resulted from certain metals being introduced into a flame. Modern scientific development has allowed atomic emission spectroscopy to become a routine method of analysis for more than 50 different elements."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Atomic emission processes are typically initiated by thermal excitation of a ground-state electron within the analyte to an excited state. The excited states are not stable and release energy to relax back to lower-energy states, producing element-specific emission lines."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "For this experiment, an Inductively Coupled Plasma (ICP) provides the thermal energy to produce atomic emission. A schematic ICP-AES system includes sample introduction, ionization source and RF generator, optical spectrometer, detector electronics, and computerized instrument control for data collection and analysis."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "experiment" }]
  }),

  createLabPassage({
    id: "experiment",
    progressLabel: "The Experiment",
    title: "The Experiment",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "As in prior laboratories, you will determine ion concentrations in solution. For this laboratory, a 5 ppm Ca2+, Mg2+, and Na+ standard solution and a blank solution are provided for calibration of the ICP-AES."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Instead of recording a full calibration curve in advance, a two-point calibration is used. For ICP-AES, calibration is linear over a wide concentration range, which supports this approach."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "For this experiment you will determine concentrations of Ca2+, Mg2+, and Na+ in tap water and bottled water."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "materials" }]
  }),

  createLabPassage({
    id: "materials",
    progressLabel: "Materials",
    title: "Virtual Materials / Equipment",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "bulletList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Previously prepared 5 ppm Ca2+, Mg2+, and Na+ standard solution"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Blank solution (1% HNO3 in deionized water)"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: "Tap water" }]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: "Bottled water" }]
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "procedure" }]
  }),

  createLabPassage({
    id: "procedure",
    progressLabel: "Procedure",
    title: "Procedure",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Select wavelengths for analysis. Wavelengths to detect atomic emission from Ca2+, Mg2+, and Na+ should be selected based on spectral intensity and minimization of overlap with other species."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                { type: "text", text: "The technique to measure samples is:" }
              ]
            },
            {
              type: "orderedList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Perform calibration of the system by running analysis on the standard solution and then the blank solution."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Perform three measurements on the tap water sample."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Perform three measurements on the bottled water sample."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Flow blank solution through system for next lab."
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "explore-instrument" }]
  }),

  createLabPassage({
    id: "explore-instrument",
    progressLabel: "Explore instrument",
    title: "Open the Lab Area",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Open the Lab Area and review sample introduction, plasma zones, optics, and detection."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "run-calibration" }],
    setFlags: (prev) => ({ ...prev, labAreaUnlocked: true })
  }),

  createLabPassage({
    id: "run-calibration",
    progressLabel: "Calibration runs",
    title: "Run standard and blank",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "On the Experiment tab, run the 5 ppm standard first, then the blank. Record Ca, Mg, and Na intensities for calibration."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "run-samples" }]
  }),

  createLabPassage({
    id: "run-samples",
    progressLabel: "Water samples",
    title: "Tap and bottled replicates",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Measure three tap-water and three bottled-water samples."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "review-data" }]
  }),

  createLabPassage({
    id: "review-data",
    progressLabel: "Review data",
    title: "Review your matrix",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Confirm you have results for standard, blank, tap x3, and bottled x3 before saving."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "save-attempt" }]
  }),

  createLabPassage({
    id: "save-attempt",
    progressLabel: "Save Attempt",
    title: "Labe Attempt Complete, Save Your Attempt",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Save your attempt from the button below, then export the CSV from Lab Attempts for external plotting and statistics."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Save Attempt", to: "data-analysis-and-discussion" }]
  }),

  createLabPassage({
    id: "data-analysis-and-discussion",
    progressLabel: "Analysis",
    title: "Data analysis and discussion",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You do not need to prepare a lengthy lab report. Prepare an abbreviated report (about 1 page) where you:"
                }
              ]
            },
            {
              type: "orderedList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(2 points) Give a brief intro to the goals and background of the lab."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(6 points) Tabulate the data and ensure no outliers are present. If any datum is treated as an outlier, clearly indicate this and provide rationale for exclusion."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(2 points) Prepare a plot of Emission Intensity vs. Ca2+, Mg2+, and Na+ that clearly distinguishes all three data series. Include a least-squares fit line and extend it to the abscissa intercept."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(4 points) Calculate concentrations and 95% confidence intervals for Ca2+, Mg2+, and Na+ in the two unknowns, reported in molarity and ppm."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(2 points) Calculate relative uncertainty and compare with instrument-provided values; discuss whether this compares with the spread of x-axis intercepts."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(2 points) Give a conclusion paragraph."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "(2 points) In appendices, tabulate all data and show sample calculations used in this laboratory."
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Optional: Give any helpful comments on how this lab may be made more relevant to ChE students in the future."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [{ text: "Next", to: "lab-complete" }]
  }),

  createLabPassage({
    id: "lab-complete",
    progressLabel: "Lab Complete",
    title: "Laboratory complete",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You have finished the interactive portion. Complete the abbreviated report and submit as directed by your instructor."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [],
    ...completionPassageExtras("lab-complete")
  })
];

/** @type {import("@/types/labManifest.types.js").LabManifest} */
export const labManifest = createLabManifest({
  id: "atomic-emission-spectrometer-lab",
  displayName: "Atomic Emission Spectrometer Lab",
  labAreaUnlockPassageId: "explore-instrument",
  completionPassageId: "lab-complete",
  passages
});

export default {
  draft: passages
};
export { passages };
